import React, { useState, useEffect } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";

import { getCustomer } from "../queries/global";

import config from "../config/config";
import * as UTILS from "../utils/utils";
import * as QUERIES from "../queries/query-functions";

import LoadingIndicator from "../components/loading-indicator";
import AccountNavigation from "./account-navigation";
import LogoutButton from "../components/logout-button";
import AvatarCircle from "../svg/avatar-circle";
import HeartCircle from "../svg/heart-circle";
import AccountIcons from "../svg/account-icons";
import UpsellBlock from "../components/upsell-block";
import PreferencesBlock from "./preferences-block";
import UpcomingTickets from "./upcoming-tickets";
import CustomerMemberhips from "./customer-memberships";

const Account = () => {
  const { data: customer, customerPending, customerFetching } = getCustomer();

  const [totalDonations, setTotalDonations] = useState(null);
  const [customerTagIds, setCustomerTagIds] = useState(
    UTILS.getCustomerTagIds(customer?.tags)
  );

  const { isPending: ordersPending, data: ordersData } = useQuery({
    queryKey: ["ordersData"],
    queryFn: QUERIES.getOrders,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (!ordersData) return;

    const totalDonations = UTILS.getTotalDonations(ordersData);
    setTotalDonations(totalDonations);
  }, [ordersData]);

  useEffect(() => {
    if (!customer) return;

    setCustomerTagIds(UTILS.getCustomerTagIds(customer?.tags));
  }, [customer]);

  if (customerPending || customerFetching)
    return <LoadingIndicator type="page" />;

  return (
    <>
      <AccountNavigation />
      <div className="booking-pathway__container booking-pathway__account booking-pathway__account--overview">
        <div className="booking-pathway__main-content">
          <h1 className="booking-pathway__header">
            <AvatarCircle /> Hello, {customer.firstName}
          </h1>

          {!customerTagIds?.length && <PreferencesBlock />}

          <div className="customer-status-blocks">
            <CustomerMemberhips />

            {totalDonations ? (
              <section className="customer-donation-history">
                <h2 className="booking-pathway__header">
                  <HeartCircle />
                  Thank you, {customer.firstName}
                </h2>
                <div className="customer-donation-history__info">
                  <p>
                    Through your support, you’ve donated…{" "}
                    <span className="customer-donation-history__donation-amount">
                      £{totalDonations.toFixed(2)}
                    </span>
                  </p>
                  {config.OPTION_FIELDS.account?.donation_history
                    ?.introduction && (
                    <div
                      className="customer-donation-history__introduction"
                      dangerouslySetInnerHTML={{
                        __html:
                          config.OPTION_FIELDS.account.donation_history
                            .introduction,
                      }}
                    ></div>
                  )}
                </div>
              </section>
            ) : null}
          </div>

          <UpcomingTickets pending={ordersPending} />

          <section className="account-overview-navigation">
            <h2 className="booking-pathway__header">Your account</h2>
            <ul className="account-overview-navigation__list" role="list">
              {config.ACCOUNT_AREAS.map((area) => {
                if (area.icon)
                  return (
                    <li key={area.link}>
                      <Link
                        to={area.link}
                        className="account-overview-navigation__list--link"
                      >
                        <AccountIcons icon={area.icon} />
                        <span>
                          <span className="account-overview-navigation__list--header">
                            {area.title}
                          </span>
                          <span>{area.description}</span>
                        </span>
                      </Link>
                    </li>
                  );
              })}
            </ul>
          </section>
        </div>

        <div className="booking-pathway__footer">
          <LogoutButton />

          {customer.subscriptions?.length ? (
            <UpsellBlock
              content={config.OPTION_FIELDS.account?.thank_you}
              type="overview-thank-you"
            />
          ) : (
            <UpsellBlock
              content={config.OPTION_FIELDS.account?.overview_upsell}
              type="overview-upsell"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Account;
